/**
 * Ticket Lifecycle v2 — Advisor Delay Impact (Slice 5A.6-lite)
 *
 * Pure helper that estimates how much of a ticket's observed advisor/FOH
 * time likely delayed the ticket lifecycle, given its job mix.
 *
 * This is **analytics math only**. It is NOT lifecycle prediction. It is
 * NOT scheduling integration. It does NOT feed Chronomancer or change
 * Tech/Bay OptiTime (`tickets.opti_time` / `tickets.opti_finish`).
 *
 * Formula table (per job-mix bucket):
 *
 *   bucket               | impact formula                                              | weight | method
 *   ---------------------|-------------------------------------------------------------|--------|-----------------------
 *   diag_only            | advisor_observed                                            | 1.0    | full_advisor_observed
 *   diag_plus_work       | max(0, advisor_observed − remaining_approved_work_minutes)  | null   | overlap_adjusted
 *   diag_plus_work       | 0   when remaining_approved_work_minutes is missing/null    | null   | overlap_adjusted
 *   work_only            | 0                                                           | 0.0    | zero_default
 *   work_with_dependency | 0   (deferred — no reliable hold/dependency event today)    | null   | dependency_deferred
 *   comeback_recheck     | advisor_observed × 0.6                                      | 0.6    | weighted_comeback
 *
 * Reasoning:
 *   - `diag_only` tickets cannot easily continue production work while
 *     FOH owns the ticket — most advisor time is true lifecycle delay.
 *   - `diag_plus_work` tickets can run technician work in parallel with
 *     advisor work; only advisor time *beyond* the remaining approved
 *     work is unambiguous delay. Without overlap data we cannot
 *     responsibly attribute delay, so we conservatively return 0 and
 *     surface the gap in `notes`.
 *   - `work_only` tickets default to no advisor lifecycle impact (the
 *     ticket sat in BOH the whole time; FOH dwell, if any, was likely
 *     a non-blocking handoff).
 *   - `work_with_dependency` will require a real dependency signal
 *     before it can be calculated; until then it is explicitly deferred.
 *   - `comeback_recheck` carries elevated advisor/customer communication
 *     risk; a medium 0.6 placeholder weight keeps the math defensible
 *     until empirical tuning is possible.
 *
 * Purity contract:
 *   - Same inputs always produce the same output.
 *   - No `Date.now()`, no `new Date()`, no I/O.
 *   - Does not mutate the input object.
 *   - Negative inputs are clamped to 0; the output is never negative.
 *
 * Boundary: this module is analytics-only. It must not be imported into
 * Chronomancer / scheduling / timer / parking / rollover / bay /
 * invariants hot paths. The drift detector boundary guard pins this
 * (`tests/unit/server/custodyEnumDriftDetector.test.ts` §9).
 */

import type { TicketJobMixBucket } from './jobMixClassifier';
import {
  ADVISOR_WEIGHT_COMEBACK,
  ADVISOR_WEIGHT_DIAG_ONLY,
  ADVISOR_WEIGHT_WORK_ONLY,
} from './jobMixClassifier';

export type AdvisorDelayImpactMethod =
  | 'full_advisor_observed'
  | 'overlap_adjusted'
  | 'zero_default'
  | 'weighted_comeback'
  | 'dependency_deferred';

export interface AdvisorDelayImpactInput {
  /** From `deriveTimeBuckets(...).advisorObservedMinutes`. */
  advisorObservedMinutes: number;
  /** From `classifyTicketJobMix(...).bucket`. */
  jobMixBucket: TicketJobMixBucket;
  /**
   * Predicted minutes of jobs that were approved-and-not-yet-completed
   * at the moment FOH took custody. Required to compute the
   * `diag_plus_work` overlap. May be omitted/null when the caller does
   * not yet have this signal — in which case the helper conservatively
   * returns 0 for that bucket and surfaces the gap in `notes`.
   */
  remainingApprovedWorkMinutes?: number | null;
}

export interface AdvisorDelayImpactResult {
  advisorDelayImpactMinutes: number;
  advisorObservedMinutes: number;
  bucket: TicketJobMixBucket;
  method: AdvisorDelayImpactMethod;
  advisorWeight: number | null;
  remainingApprovedWorkMinutes: number | null;
  notes: string[];
}

function clampNonNegative(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return value;
}

/**
 * Estimate advisor delay impact for a ticket. Pure: same inputs always
 * produce the same output.
 */
export function calculateAdvisorDelayImpact(
  input: AdvisorDelayImpactInput,
): AdvisorDelayImpactResult {
  const advisorObservedMinutes = clampNonNegative(input.advisorObservedMinutes);
  const hasOverlapInput =
    input.remainingApprovedWorkMinutes !== undefined &&
    input.remainingApprovedWorkMinutes !== null;
  const remainingApprovedWorkMinutes = hasOverlapInput
    ? clampNonNegative(input.remainingApprovedWorkMinutes as number)
    : null;

  switch (input.jobMixBucket) {
    case 'diag_only': {
      return {
        advisorDelayImpactMinutes: advisorObservedMinutes,
        advisorObservedMinutes,
        bucket: 'diag_only',
        method: 'full_advisor_observed',
        advisorWeight: ADVISOR_WEIGHT_DIAG_ONLY,
        remainingApprovedWorkMinutes,
        notes: [
          'Inspection/diag-only tickets usually cannot continue production work while FOH acts; full advisor observed time counts as lifecycle delay.',
        ],
      };
    }

    case 'diag_plus_work': {
      if (remainingApprovedWorkMinutes === null) {
        return {
          advisorDelayImpactMinutes: 0,
          advisorObservedMinutes,
          bucket: 'diag_plus_work',
          method: 'overlap_adjusted',
          advisorWeight: null,
          remainingApprovedWorkMinutes: null,
          notes: [
            'Remaining approved work minutes unavailable; advisor delay impact deferred to avoid overstating overlap-adjusted delay.',
          ],
        };
      }
      const impact = clampNonNegative(
        advisorObservedMinutes - remainingApprovedWorkMinutes,
      );
      return {
        advisorDelayImpactMinutes: impact,
        advisorObservedMinutes,
        bucket: 'diag_plus_work',
        method: 'overlap_adjusted',
        advisorWeight: null,
        remainingApprovedWorkMinutes,
        notes: [
          'Advisor time is reduced by remaining approved work that could overlap on this ticket.',
        ],
      };
    }

    case 'work_only': {
      return {
        advisorDelayImpactMinutes: 0,
        advisorObservedMinutes,
        bucket: 'work_only',
        method: 'zero_default',
        advisorWeight: ADVISOR_WEIGHT_WORK_ONLY,
        remainingApprovedWorkMinutes,
        notes: [
          'Work-only tickets default to no advisor lifecycle delay unless a dependency/hold is present.',
        ],
      };
    }

    case 'work_with_dependency': {
      return {
        advisorDelayImpactMinutes: 0,
        advisorObservedMinutes,
        bucket: 'work_with_dependency',
        method: 'dependency_deferred',
        advisorWeight: null,
        remainingApprovedWorkMinutes,
        notes: [
          'Dependency-driven advisor delay requires reliable hold/dependency events and is deferred.',
        ],
      };
    }

    case 'comeback_recheck': {
      return {
        advisorDelayImpactMinutes: advisorObservedMinutes * ADVISOR_WEIGHT_COMEBACK,
        advisorObservedMinutes,
        bucket: 'comeback_recheck',
        method: 'weighted_comeback',
        advisorWeight: ADVISOR_WEIGHT_COMEBACK,
        remainingApprovedWorkMinutes,
        notes: [
          'Comeback/recheck tickets carry medium advisor/customer communication impact (0.6 placeholder until tuned from data).',
        ],
      };
    }
  }
}
